<template>
  <span>
    <slot name="button" v-bind="{ onclick: toggle }">
      <b-button
        v-if="create"
        type="button"
        class="mr-1"
        variant="success"
        size="xs"
        @click="toggle"
      >
        <i class="fas fa-plus" /> Create
      </b-button>
      <b-button
        v-else
        type="button"
        class="mr-1"
        variant="primary"
        size="xs"
        @click="toggle"
      >
        <i class="fas fa-edit" />&nbsp;Edit
      </b-button>
    </slot>

    <b-modal
      @show="resetModal"
      @hidden="resetModal"
      ref="modal-container"
      v-model="modalShow"
      size="lg"
      :title="title"
      :static="true"
      :lazy="true"
      hide-footer
    >
      <form ref="form" @submit.stop.prevent="handleSubmit">
        <b-form-group label="UID" label-for="uid-input">
          <b-form-input
            id="uid-input"
            v-model="formValues.uid"
            autocomplete="off"
            :disabled="!create"
          />
        </b-form-group>

        <b-row>
          <b-col>
            <b-form-group label="Operator" label-for="operator-input">
              <multiselect
                id="operator-input"
                v-model="selectedOperator"
                :options="operatorOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select operator"
                :disabled="!create"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col>
            <b-form-group label="Vip-Limit" label-for="limitType-input">
              <multiselect
                id="limitType-input"
                v-model="selectedLimitType"
                :options="vipLimitTypeOptions"
                :searchable="true"
                :internal-search="true"
                label="text"
                track-by="value"
                deselect-label=""
                select-label="Select"
                placeholder="Select vip limit type"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="create">
          <b-form-group label="Tables" label-for="tables-input">
            <multiselect
              id="tables-input"
              v-model="selectedTables"
              :options="tableOptions"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              label="text"
              track-by="value"
              deselect-label=""
              select-label="Select"
              placeholder="Select tables"
              :preserve-search="true"
            />
            <b-button
              class="mt-2 mr-1"
              variant="light"
              size="sm"
              type="button"
              @click="selectAllTables"
            >
              Select All
            </b-button>
            <b-button
              class="mt-2"
              variant="light"
              size="sm"
              type="button"
              @click="unselectAllTables"
            >
              Unselect All
            </b-button>
          </b-form-group>
        </div>

        <div v-else class="mb-2">
          <b-form-group label="Table" label-for="table-input">
            <multiselect
              id="table-input"
              v-model="selectedTable"
              :options="tableOptions"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="false"
              label="text"
              track-by="value"
              deselect-label=""
              select-label="Select"
              placeholder="Select table"
            />
          </b-form-group>
        </div>

        <b-button
          class="mt-2 float-right"
          variant="primary"
          type="button"
          @click="handleSubmit"
          :disabled="!canSubmit"
        >
          Save
        </b-button>
      </form>
    </b-modal>
  </span>
</template>

<script>
import Multiselect from 'vue-multiselect'
import { LIMIT_TYPES } from '@/constants/LimitTypes'

export default {
  components: {
    Multiselect
  },
  props: {
    create: Boolean,
    title: String,
    defaultValues: Object
  },
  data() {
    return {
      modalShow: false,
      formValues: this.getInitialValues(),
      settingsLoading: false
    }
  },
  methods: {
    getInitialValues() {
      return {
        uid: '',
        operatorId: null,
        tableId: null,
        originalTableId: null,
        limitType: null,
        limitName: null,
        tableIds: []
      }
    },
    resetModal() {
      this.setDefaults()
      this.$nextTick(() => {
        if (this.$validator) {
          this.$validator.reset()
        }
      })
    },
    setDefaults() {
      const initialValues = this.getInitialValues()

      // deep clone simple values
      for (const key in initialValues) {
        this.$set(this.formValues, key, initialValues[key])
      }

      if (this.defaultValues) {
        const cloned = JSON.parse(JSON.stringify(this.defaultValues))
        for (const key in cloned) {
          this.$set(this.formValues, key, cloned[key])
        }
      }

      if (!this.create && this.defaultValues?.vipLimitType !== undefined) {
        this.formValues.limitType = this.defaultValues.vipLimitType
      }

      if (!this.create) {
        this.formValues.originalTableId = this.formValues.tableId
      }
    },
    toggle() {
      if (!this.modalShow) {
        this.setDefaults()
      }
      this.modalShow = !this.modalShow
    },
    hideModal() {
      this.$refs['modal-container'].hide()
    },
    selectAllTables() {
      this.formValues.tableIds = this.tableOptions.map(opt => parseInt(opt.value, 10))
    },
    unselectAllTables() {
      this.formValues.tableIds = []
    },
    async handleSubmit() {
      const uid = (this.formValues.uid || '').toString().trim()
      const operatorId = this.formValues.operatorId

      if (!uid || !operatorId) {
        this.$toastr.e('UID and Operator are required')
        return
      }

      if (this.formValues.limitType === null || this.formValues.limitType === undefined) {
        this.$toastr.e('Vip-Limit is required')
        return
      }

      if (this.create) {
        const tableIds = this.selectedTables.map(t => parseInt(t.value))
        if (!tableIds.length) {
          this.$toastr.e('At least one table must be selected')
          return
        }
        this.formValues.tableIds = tableIds
      } else {
        if (!this.formValues.tableId) {
          this.$toastr.e('Table is required')
          return
        }
      }

      const payload = {
        uid,
        operatorId,
        limitType: this.formValues.limitType,
        applyToAllTables: false,
        tableIds: this.create ? this.formValues.tableIds : undefined
      }

      try {
        if (this.create) {
          await this.$http({
            url: '/api/v1/player-vip-limits',
            method: 'POST',
            data: payload,
            headers: { 'Content-Type': 'application/json' }
          })
        } else {
          const oldTableId = this.formValues.originalTableId
          const newTableId = this.formValues.tableId

          if (oldTableId && newTableId && String(oldTableId) !== String(newTableId)) {
            await this.$http({
              url: `/api/v1/player-vip-limits/${encodeURIComponent(
                this.formValues.uid
              )}/${this.formValues.operatorId}/${oldTableId}`,
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' }
            })
          }

          await this.$http({
            url: `/api/v1/player-vip-limits/${encodeURIComponent(
              this.formValues.uid
            )}/${this.formValues.operatorId}/${newTableId}`,
            method: 'PUT',
            data: {
              limitType: this.formValues.limitType
            },
            headers: { 'Content-Type': 'application/json' }
          })
        }

        this.$emit('finish')
        this.hideModal()
        this.$toastr.i('Success')
      } catch (err) {
        this.$toastr.e(JSON.stringify(err?.response?.data?.message || err))
      }
    }
  },
  computed: {
    canSubmit() {
      return true
    },
    operatorOptions() {
      return (
        this.$store.getters['limits/operatorsForSelect']?.map(option => {
          return { value: option.value, text: option.label }
        }) || []
      )
    },
    tableOptions() {
      return (
        this.$store.getters['tablesForSelect/list']?.map(opt => {
          return { value: opt.value, text: `${opt.value} - ${opt.label}` }
        }) || []
      )
    },
    vipLimitTypeOptions() {
      return LIMIT_TYPES.filter(t => t.value !== 0)
    },
    selectedOperator: {
      get() {
        if (!this.formValues.operatorId) return null
        return this.operatorOptions.find(opt => opt.value === this.formValues.operatorId) || null
      },
      set(value) {
        this.formValues.operatorId = value?.value || null
      }
    },
    selectedLimitType: {
      get() {
        if (this.formValues.limitType === null || this.formValues.limitType === undefined) return null
        return (
          this.vipLimitTypeOptions.find(opt => opt.value === this.formValues.limitType) || null
        )
      },
      set(value) {
        this.formValues.limitType = value?.value ?? null
      }
    },
    selectedTables: {
      get() {
        // v-model for multiselect expects full objects
        const ids = this.formValues.tableIds || []
        return this.tableOptions.filter(opt => ids.map(x => parseInt(x)).includes(parseInt(opt.value)))
      },
      set(value) {
        // value is array of option objects
        this.formValues.tableIds = (value || []).map(v => parseInt(v.value))
      }
    },
    selectedTable: {
      get() {
        if (!this.formValues.tableId) return null
        return this.tableOptions.find(opt => String(opt.value) === String(this.formValues.tableId)) || null
      },
      set(value) {
        this.formValues.tableId = value?.value ? parseInt(value.value) : null
      }
    }
  },
  async beforeMount() {
    await this.$store.dispatch('tablesForSelect/loadList').catch(() => {})
    await this.$store.dispatch('limits/loadOperatorsForSelect').catch(() => {})
  }
}
</script>

